import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { can } from '../utils/roles';

export default function MetaSettings() {
  const { user } = useAuth();
  const [config, setConfig] = useState({ app_id: '', app_secret: '', access_token: '', verify_token: '', page_id: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/api/meta/config').then(r => setConfig(c => ({...c, ...r.data}))).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.post('/api/meta/config', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { alert('Error saving'); }
  };

  if (!can.metaAds(user?.role)) return <div className="glass rounded-xl p-8 text-center text-dark-400">Access restricted.</div>;

  const webhookUrl = `https://kapsonsinvestments.com/api/meta/webhook`;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-xl font-bold text-white">Meta Ads Integration</h3>
        <p className="text-dark-400 text-sm">Connect Facebook/Instagram Lead Ads to auto-import leads</p>
      </div>

      <div className="glass rounded-xl p-5 space-y-3">
        <h4 className="text-white font-medium border-b border-dark-700 pb-2">Webhook URL (add this to Meta)</h4>
        <div className="flex items-center gap-2 p-3 bg-dark-800 rounded-lg">
          <code className="text-green-400 text-sm flex-1 break-all">{webhookUrl}</code>
          <button onClick={() => navigator.clipboard.writeText(webhookUrl)} className="px-3 py-1 bg-dark-700 text-dark-300 rounded text-xs">Copy</button>
        </div>
        <p className="text-dark-400 text-xs">In Meta Developer Console → Your App → Webhooks → Subscribe to <strong>leadgen</strong> events. Paste this URL and your Verify Token below.</p>
      </div>

      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium">Meta App Credentials</h4>
        {[
          ['App ID', 'app_id', 'text', 'From Meta Developer Console'],
          ['App Secret', 'app_secret', 'password', 'Keep this secret'],
          ['Page Access Token', 'access_token', 'password', 'Long-lived page token'],
          ['Page ID', 'page_id', 'text', 'Your Facebook Page ID'],
          ['Verify Token', 'verify_token', 'text', 'Create any random string — paste same in Meta'],
        ].map(([label, key, type, hint]) => (
          <div key={key}>
            <label className="block text-sm text-dark-300 mb-1">{label}</label>
            <input type={type} value={config[key] || ''} onChange={e => setConfig({...config, [key]: e.target.value})}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
            <p className="text-dark-500 text-xs mt-0.5">{hint}</p>
          </div>
        ))}
        <button onClick={save} className={`px-6 py-2 rounded-lg text-sm font-medium ${saved ? 'bg-green-500 text-white' : 'bg-primary-500 text-dark-900 hover:bg-primary-600'}`}>
          {saved ? '✓ Saved!' : 'Save Configuration'}
        </button>
      </div>

      <div className="glass rounded-xl p-5">
        <h4 className="text-white font-medium mb-2">Setup Steps</h4>
        <ol className="space-y-2 text-sm text-dark-400 list-decimal list-inside">
          <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-blue-400">developers.facebook.com</a> → Create App</li>
          <li>Add "Leads Access" product to your app</li>
          <li>Go to Webhooks → Add webhook → Page → Subscribe to <strong>leadgen</strong></li>
          <li>Paste the webhook URL above and your Verify Token</li>
          <li>Generate a long-lived Page Access Token from Graph API Explorer</li>
          <li>Save all credentials here and test with a real Facebook ad</li>
        </ol>
      </div>
    </div>
  );
}
